import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import {
  SET_PASSOWORD,
  DELETE_PASSWORD,
  GET_PASSWORDS,
} from "../../api/password";
import { decryptData } from "../../utils/decrypt.utils";

const generateRandomPassword = (length = 12) => {
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$!%&*?";
  const array = new Uint32Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (x) => charset[x % charset.length]).join("");
};

const LockedPages = ({ isAuth, setIsAuth, a_password }) => {
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({ path: "", password: "" });
  const [randomLength, setRandomLength] = useState(12);

  const fetchData = async () => {
    try {
      const res = await axios.get(GET_PASSWORDS);
      // Decrypt passwords before setting rows
      const decryptedRows = res.data.map((row) => ({
        ...row,
        password: JSON.parse(decryptData(row.password)),
      }));
      setRows(decryptedRows);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (a_password) fetchData();
  }, [a_password]);

  if (!a_password) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          ❌ You are not allowed to view this page.
        </Typography>
      </Box>
    );
  }
  const handleDelete = async (path) => {
    await axios.delete(`${DELETE_PASSWORD}${encodeURIComponent(path)}`);
    fetchData();
  };

  const handleOpenModal = (row = { path: "", password: "" }) => {
    setFormData({
      path: row.path,
      password: row.password,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setFormData({ path: "", password: "" });
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    const { path, password } = formData;
    await axios.post(SET_PASSOWORD, { path, password });
    handleCloseModal();
    fetchData();
  };

  const handleGenerateRandom = () => {
    const newPassword = generateRandomPassword(randomLength);
    setFormData((prev) => ({ ...prev, password: newPassword }));
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const columns = [
    { field: "path", headerName: "Path", flex: 1 },
    {
      field: "password",
      headerName: "Password",
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginRight: 8 }}>{row.password}</span>
          <Tooltip title="Copy Password">
            <IconButton onClick={() => handleCopy(row.password)} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "expiresAt",
      headerName: "Expiry Date",
      flex: 1,
      valueGetter: ({ row }) => new Date(row.expiresAt).toLocaleString(),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => (
        <span style={{ color: value === "expired" ? "red" : "green" }}>
          {value}
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }) => (
        <Box>
          <Button onClick={() => handleOpenModal(row)} size="small">
            Update
          </Button>
          <Button
            onClick={() => handleDelete(row.path)}
            size="small"
            color="error"
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        🔒 Locked Pages
      </Typography>

      <Button
        variant="contained"
        color="primary"
        onClick={() => handleOpenModal()}
      >
        + Create Password
      </Button>

      <Box sx={{ mt: 3, height: 500 }}>
        <DataGrid
          rows={rows.map((r, i) => ({ ...r, id: i }))}
          columns={columns}
          pageSize={10}
          getRowClassName={(params) =>
            params.row.status === "expired" ? "expired-row" : ""
          }
        />
      </Box>

      {/* Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal}>
        <DialogTitle>
          {formData?.path ? "Update" : "Create"} Password
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Path"
            value={formData.path}
            onChange={(e) => setFormData({ ...formData, path: e.target.value })}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              margin="dense"
              label="Password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
            <Tooltip title="Copy Password">
              <IconButton
                onClick={() => handleCopy(formData.password)}
                sx={{ mt: 1 }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <TextField
              type="number"
              label="Length"
              size="small"
              value={randomLength}
              onChange={(e) => setRandomLength(parseInt(e.target.value))}
            />
            <Button onClick={handleGenerateRandom} variant="outlined">
              Generate Random
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          .expired-row {
            background-color: #ffe6e6 !important;
          }
        `}
      </style>
    </Box>
  );
};

export default LockedPages;
